import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
  addChild,
  addComponent,
  AmbientLightComponent,
  ApiService,
  CircleSegments,
  clone,
  createv3dObject,
  findInTree,
  getParent,
  globalV3dSettings,
  removeChild,
  V3dOrthographicViewComponent,
  V3dPerspectiveViewComponent,
  ViewPoint,
  Visualize3dObject,
  Visualize3dObjectLoadEvent,
  Visualize3dObjectLoadStatus,
  Visualize3dViewerOptions,
} from 'projects/visualize3d/src/public-api';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { saveAs } from 'file-saver';
import { PreferencesDialogComponent } from './components/preferences-dialog/preferences-dialog.component';
import { AboutDialogComponent } from './components/about-dialog/about-dialog.component';
import { environment } from '../environments/environment';
import type { ImportV3dObject } from './models/import-v3d-object';
import { Visualize3dObjectEventService } from '../../../visualize3d/src/lib/providers/visualize3d-object-event.service';
import { Subscription } from 'rxjs';
import { Visualize3dComponentFactoryService } from '../../../visualize3d/src/lib/providers/visualize3d-component-factory.service';
import { ConfigService } from '../../../visualize3d/src/lib/providers/config.service';
import { DxfExportMode } from 'projects/visualize3d/src/lib/models/dxf-export-mode';
import { QModelViewMode } from 'projects/visualize3d/src/lib/models/q-model-view-mode';
import {
  createDefaultPerspectiveCamera,
  V3dPerspectiveSceneManager,
} from '../../../visualize3d/src/lib/scene/v3d-perspective-scene-manager';
import { createDefaultV3DRenderer } from '../../../visualize3d/src/lib/scene/v3d-scene-manager';
import { Scene } from 'three';
import {
  createDefaultOrthographicCamera,
  V3dOrthographicSceneManager,
} from '../../../visualize3d/src/lib/scene/v3d-orthographic-scene-manager';

globalV3dSettings.debug = !environment.production;
globalV3dSettings.showDebugStats = globalV3dSettings.debug;
globalV3dSettings.showDebugAxes = globalV3dSettings.debug;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  apiService = inject(ApiService);
  private configService = inject(ConfigService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private visualize3dObjectEventService = inject(Visualize3dObjectEventService);
  private componentFactoryService = inject(Visualize3dComponentFactoryService);

  @ViewChild('orthographicView', { static: false }) orthographicView: V3dOrthographicViewComponent;
  @ViewChild('perspectiveView', { static: false }) perspectiveView: V3dPerspectiveViewComponent;

  public root: Visualize3dObject;

  public selectedV3dObject: Visualize3dObject | undefined;
  public navigation: Visualize3dViewerOptions = {
    hasInteractions: true,
  };

  public isOrthographic = false;
  public view: ViewPoint = ViewPoint.TopLeft;

  private isFirstLoad = true;

  private subscriptions: Subscription[] = [];
  perspectiveSceneManager: V3dPerspectiveSceneManager;
  orthographicSceneManager: V3dOrthographicSceneManager;

  ngOnInit(): void {
    this.subscriptions.push(
      this.visualize3dObjectEventService.v3dObjectSelectionChanged.subscribe(event => {
        this.selectedV3dObject = event.v3dObject;
      }),
    );

    this.root = createv3dObject('Root');
    this.componentFactoryService.create<AmbientLightComponent>('AmbientLight').subscribe(component => {
      addComponent(this.root, component);
    });

    const renderer = createDefaultV3DRenderer();
    this.perspectiveSceneManager = new V3dPerspectiveSceneManager(createDefaultPerspectiveCamera(1600, 900), new Scene(), renderer);
    this.orthographicSceneManager = new V3dOrthographicSceneManager(createDefaultOrthographicCamera(1600, 900), new Scene(), renderer);
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.tryUpdateCanvasSize();
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Delete' && event.target && event.target instanceof Element) {
      const element = event.target as Element;
      if (element.nodeName === 'INPUT') {
        return;
      }
      this.removeSelectedV3dObject();
    }
  }

  public componentChange(): void {
    this.isFirstLoad = true;
    this.refreshV3dObject();
    this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
  }

  public addObject3d(): void {
    const object3d = createv3dObject('Empty object');
    if (this.selectedV3dObject) {
      addChild(this.selectedV3dObject, object3d);
    } else if (this.root) {
      addChild(this.root, object3d);
    } else {
      this.root = object3d;
    }
    this.isFirstLoad = true;
    this.selectedV3dObject = object3d;
    this.refreshV3dObject();
    this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
  }

  public removeV3dObject(v3dObject: Visualize3dObject): void {
    if (v3dObject === this.root) {
      this.root = null;
      this.selectedV3dObject = null;
      this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
      return;
    }
    if (this.selectedV3dObject === v3dObject) {
      this.selectedV3dObject = null;
    }
    const parent = getParent(this.root, v3dObject);
    removeChild(parent, v3dObject);
    this.refreshV3dObject();
    this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
  }

  public removeSelectedV3dObject(): void {
    if (!this.selectedV3dObject) {
      return;
    }
    this.removeV3dObject(this.selectedV3dObject);
  }

  public viewChange(view: ViewPoint | null): void {
    this.view = view;
    const sceneManager = this.getActiveSceneManager();
    sceneManager.lookAtObjectFromViewPoint(view, sceneManager.getRootObject());
  }

  public fitChange(fit: boolean): void {
    const navigation: Visualize3dViewerOptions = this.navigation ? JSON.parse(JSON.stringify(this.navigation)) : {};
    const sceneManager = this.getActiveSceneManager();
    sceneManager.lookAtObjectFromViewPoint(this.view, sceneManager.getRootObject(), fit);
    this.navigation = navigation;
  }

  public orthographicChange(isOrthographic: boolean): void {
    if (isOrthographic !== this.isOrthographic) {
      this.isFirstLoad = true;
    }
    this.isOrthographic = isOrthographic;
    setTimeout(() => {
      this.tryUpdateCanvasSize();
    }, 1);
  }

  public on3DViewError(errorMessage: string): void {
    this.snackBar.open(errorMessage, 'Ok');
  }

  public export(type: string): void {
    switch (type) {
      case 'json':
        this.downLoadFile(JSON.stringify(this.root), 'application/json', `${this.root.name}.json`);
        return;
      case 'dxf':
        this.apiService
          .exportDxf(this.root, { dxfExportMode: DxfExportMode.Cad3D, view: ViewPoint.Front, circleSegments: CircleSegments.Segments64 })
          .subscribe(
            response => this.downLoadFile(response, 'application/dxf', `${this.root.name}.dxf`),
            error => this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(error)}`, 'Ok'),
          );
        return;
      case 'jpg':
      case 'png':
        this.apiService
          .exportImage(this.root, {
            format: type,
            width: 1024,
            height: 1024,
            view: ViewPoint.TopLeft,
            circleSegments: CircleSegments.Segments64,
            backgroundColor: '#ffffff',
            renderMode: QModelViewMode.Solid,
          })
          .subscribe(
            response => this.downLoadFile(response, `application/${type}`, `${this.root.name}.${type}`),
            error => this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(error)}`, 'Ok'),
          );
        return;
      case 'gmmodel':
        this.apiService.exportGmModel(this.root, { view: ViewPoint.Front }).subscribe(
          response => this.downLoadFile(response, `application/octet-stream`, `${this.root.name}.gmmodel`),
          error => this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(error)}`, 'Ok'),
        );
        return;
      case 'qmd':
        this.apiService.exportQModel(this.root).subscribe(
          response => this.downLoadFile(response, `application/octet-stream`, `${this.root.name}.qmd`),
          error => this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(error)}`, 'Ok'),
        );
        return;
      case 'svg':
        this.apiService.exportSvg(this.root, { view: ViewPoint.Front }).subscribe(
          response => this.downLoadFile(response, `application/svg+xml`, `${this.root.name}.svg`),
          error => this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(error)}`, 'Ok'),
        );
        return;
    }
    throw new Error(`Export type [${type}] is not supported`);
  }

  public importObject3d(importObject: ImportV3dObject): void {
    if (importObject.error) {
      this.snackBar.open(`Oops... something went wrong. ${JSON.stringify(importObject.error)}`, 'Ok');
      return;
    }
    this.root = importObject.v3dObject;
    this.selectedV3dObject = null;
    this.refreshV3dObject();
    this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
    this.viewChange(ViewPoint.Front);
    this.fitChange(true);
    this.snackBar.open(`Successfully imported ${importObject.fileName}`, 'Ok');
  }

  public refreshV3dObject(): void {
    // Update object reference to trigger Angular change detection
    this.root = clone(this.root);
    if (this.selectedV3dObject) {
      this.selectedV3dObject = findInTree(this.root, c => c.id === this.selectedV3dObject.id);
    }
  }

  public openPreferences(): void {
    const dialogRef = this.dialog.open(PreferencesDialogComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe((apiRoot: string) => {
      if (apiRoot) {
        this.configService.setApiRoot(apiRoot);
        this.apiService.clearCache();
        this.refreshV3dObject();
        this.visualize3dObjectEventService.updateSelectedObject(this.selectedV3dObject);
      }
    });
  }

  public openAbout(): void {
    this.dialog.open(AboutDialogComponent, {
      width: '800px',
    });
  }

  private tryUpdateCanvasSize(): void {
    this.orthographicView?.updateCanvasSize();
    this.perspectiveView?.updateCanvasSize();
  }

  private getActiveSceneManager() {
    return this.isOrthographic ? this.orthographicSceneManager : this.perspectiveSceneManager;
  }

  /**
   * Method is use to download file.
   * @param data - Array Buffer data
   * @param type - type of the document.
   * @param fileName
   */
  private downLoadFile(data: any, type: string, fileName: string): void {
    const blob = new Blob([data], { type });
    saveAs(blob, fileName);
  }

  onObject3DLoadStatus(event: Visualize3dObjectLoadEvent) {
    if (event.status === Visualize3dObjectLoadStatus.Loaded && this.isFirstLoad) {
      const sceneManager = this.getActiveSceneManager();
      sceneManager.lookAtObjectFromViewPoint(this.view, sceneManager.getRootObject(), true);
      this.isFirstLoad = false;
    }
  }
}
